import { diag } from '@/lib/diagnostics'

/**
 * Samples the page's memory every few seconds onto the console, for comparing the two hybrid modes
 * (KAN-3; docs/decisions.md D-38, D-42).
 *
 * D-42's whole diagnosis was a number nobody in this app could read - WebKit's per-tab ceiling - and
 * every decision since was made from a tab that died. This puts the number that CAN be read beside the
 * scan's own log lines:
 *
 * - `performance.measureUserAgentSpecificMemory()` where it exists (Chromium, and only on a
 *   cross-origin-isolated page, which this one is for Mindset's sake). It counts WASM memories and
 *   workers, which is where both vendors live.
 * - `performance.memory.usedJSHeapSize` otherwise (older Chromium): the JS heap only, and labelled so.
 * - Nothing on Safari, which exposes neither. On an iPhone the evidence is still the tab kill and a
 *   JetsamEvent report (D-54), and the probe says once that it has nothing to report.
 *
 * Diagnostics only: it changes nothing about the scan, and it holds no references to vendor objects
 * (D-38's lesson about a console that retains what it prints).
 *
 * @returns A stop function, which logs the peak.
 */
export function startMemoryProbe(label: string, intervalMs = 5_000): () => void {
  const performanceWithMemory = performance as Performance & {
    measureUserAgentSpecificMemory?: () => Promise<{ bytes: number }>
    memory?: { usedJSHeapSize: number }
  }
  const startedAt = Date.now()
  let peakMb = 0
  let inFlight = false

  const canMeasureEverything =
    typeof performanceWithMemory.measureUserAgentSpecificMemory === 'function' && globalThis.crossOriginIsolated === true
  const source = canMeasureEverything ? 'ua-specific' : performanceWithMemory.memory ? 'js-heap-only' : null

  if (!source) {
    console.log(`[memory] ${label}: this browser reports no memory figures`)
    diag('memory', 'not reported by this browser')
    return () => {}
  }

  const record = (bytes: number) => {
    const mb = Math.round(bytes / 1_048_576)
    peakMb = Math.max(peakMb, mb)
    console.log(`[memory] ${label}`, { mb, peakMb, source, afterMs: Date.now() - startedAt })
    diag('memory', { mb, peakMb, source })
  }

  const sample = () => {
    if (canMeasureEverything) {
      if (inFlight) return
      inFlight = true
      performanceWithMemory
        .measureUserAgentSpecificMemory!()
        .then((result) => record(result.bytes))
        .catch(() => {})
        .finally(() => {
          inFlight = false
        })
    } else if (performanceWithMemory.memory) {
      record(performanceWithMemory.memory.usedJSHeapSize)
    }
  }

  sample()
  const timer = setInterval(sample, intervalMs)

  return () => {
    clearInterval(timer)
    console.log(`[memory] ${label} ended`, { peakMb, source, afterMs: Date.now() - startedAt })
  }
}
